import { inject, Injectable, type OnDestroy } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { IntegrationMapping, IVendorMapping } from '@xilo-mono/integration-contracts';
import { concatMap, delay, exhaustMap, map, ReplaySubject, retryWhen, takeUntil, tap } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { ConfigService, type InterceptorWithWriteEvents } from '@xilo-mono/common/config';
import { iif, throwError, of, Observable } from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';
import type { IntegrationMappingResponse, Vendor } from "../models";
import { IDataPullType } from '../models/submission.model';
import { getCachedRequest } from "../utils";
import _ from 'lodash';

export interface JsonbType { [key: string]: any }

export type RaterResult = {
  quote: string,
  quote_number: string,
}

export const RateRequestStatuses = <const>['Created', 'Waiting', 'Running', 'Failed', 'Done', 'Cancelled']
export type  RateRequestStatus  = typeof RateRequestStatuses[number]

export const PolicyProductLines = <const>[
  'commercial_auto',
  'auto',
  'home',
  'umbrella',
  'flood',
  'rv',
  'motorcycle',
  'watercraft',
  'pet',
  'dwelling_fire',
  'earthquake',
  'life',
  'health',
  'bop',
  'general_liability',
  'workers_compensation',
  'commercial_property',
  'personal_package',
  'commercial_package',
  'truckers',
  'transportation'
]
export type  PolicyProductLine  = typeof PolicyProductLines[number]

export const StateTypes = <const>[
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY'
]
export type StateType = (typeof StateTypes)[number]

export interface RateRequest {
  readonly id     ?: number
  status          ?: RateRequestStatus
  orgId            : number
  clientId         : number
  insuranceDataId ?: number
  submissionId    ?: number
  formViewKey     ?: string
  lob              : PolicyProductLine
  usState         ?: StateType
  data            ?: any
  jobs            ?: RateJob[]
  carrierIds       : number[]
  execMode         : 'live' | 'test'  | 'save'
  createdById     ?: number
  createdByRole   ?: 'admin' | 'agent' | 'system' | 'client'
}

export interface RateJob {
  type            : 'Reports' | 'Quote' | 'Latest'
  status          : 'Created' | 'Waiting' | 'Running' | 'Failed' | 'Done' | 'Cancelled'
  orgId           : number
  clientId        : number
  insuranceDataId : number
  submissionId   ?: number
  requestId       : number
  carrierId       : number
  lob             : PolicyProductLine
  usState         : StateType
  data            : JsonbType
  execMode        : 'live' | 'test'  | 'save'
  error          ?: string | null
  resultedAt     ?: Date | null
  carrier        ?: JsonbType
}

@Injectable()
export class IntegrationMappingService implements OnDestroy {
  apiUrl = `${this.configService.apiUrl}/integration-mapping`;
  workflowApiUrl = `${this.configService.workflowsApiUrl}`;
  private readonly vendorSchemaUrl = `${this.configService.apiUrl}/vendor-schema`;
  queryParams: Params = Object.assign({}, this.route.snapshot.queryParams);
  pullData = new BehaviorSubject<{ id: number; index: number; openLoadDialog?: boolean, type:IDataPullType }>(null);
  i = 0;
  token = localStorage.getItem('token');
  options = {
    headers: { 'x-access-token': this.token },
  };
  private readonly interceptors = inject(HTTP_INTERCEPTORS);
  private readonly interceptor = this.interceptors[0] as unknown as InterceptorWithWriteEvents;
  private readonly writeRequestEvent$ = this.interceptor.writeRequestEvent$;
  private readonly cachedGetFormIntegrationsMapping = new Map<string, Observable<IntegrationMapping[]>>();
  private readonly cachedGetVendorsList = new Map<string, Observable<Vendor[]>>();
  private readonly destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private route: ActivatedRoute,
    ) {}

  initDataPull(
    pullDataTrigger: boolean,
    dataPullMappingId: number,
    subIndex: number,
    openLoadDialog = true,
    type:IDataPullType = { type:'DATA', loadSeconds:10 }
  ) {
    let index = null;
    if (pullDataTrigger && dataPullMappingId) {
      this.queryParams = Object.assign({}, this.route.snapshot.queryParams);
      if (this.queryParams.subsection) {
        subIndex = +this.queryParams.subsection;
      }
      if (subIndex || subIndex === 0) index = subIndex;
      
      // Add 300ms delay before triggering data pull
      setTimeout(() => {
        console.log('### Triggering Data Pull', dataPullMappingId, index, openLoadDialog, type);
        this.triggerPullData(dataPullMappingId, index, openLoadDialog, type);
      }, 400);
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  triggerPullData(id: number, index: number, openLoadDialog = true, type:IDataPullType) {
    this.pullData.next({ id, index, openLoadDialog, type});
  }

  pullDataByIntegrationMapping(
    submission: any,
    integrationMappingId: number,
    formId: string,
    clientId: string,
    companyId: string,
    index: number,
    submissionId: number
  ) {
    this.token = localStorage.getItem('token');
    this.options = {
      headers: { 'x-access-token': this.token },
    };
    return this.http
      .post(
        `${this.apiUrl}/data-pull`,
        {
          submission,
          integrationMappingId,
          formId,
          clientId,
          companyId,
          index,
          submissionId,
        },
        this.options
      )
      .pipe(map((result) => result && result['jobId']));
  }

  triggerSDKIntegration(
    submissionData: any,
    integrationMappingId: number,
    formViewId: string,
    clientId: number,
    companyId: string,
    index: number,
    submissionId: number,
    orgId: number,
    job: 'getClientAndPolicies' | 'getPolicyData' | 'push',
    lineType: 'personal' | 'commercial',
    logId?: string,
    selectedValues?: {
      ClientID?: number,
      PolicyID?: number,
    },
  ) {
    this.token = localStorage.getItem('token');
    this.options = {
      headers: { 'x-access-token': this.token },
    };
    return this.http
      .post(
        `${this.workflowApiUrl}/org/${orgId}/sdk/policy/integration`,
        {
          submissionData,
          integrationMappingId,
          formViewId,
          clientId,
          companyId,
          index,
          submissionId,
          job,
          selectedValues,
          logId,
          lineType
        },
        this.options
      ).pipe(
        map((res) => {
          return res['logId'];
        })
      );
  }

  pollSDKLogStatusById(id: any, isClientAndPoliciesPull: boolean, isPoliciesOnlyPull: boolean, retries?:number) {
    return this.http.get(`${this.configService.apiUrl}/logs/list-one/${id}`)
      .pipe(
        map((res:JsonbType) => {
          const log = res['obj']
          console.log('Polling SDK Integration: %o', log);
          if (log.result === 'Failed' || log.result === 'Succeed') return log
          // Return log if array of policies has been found
          if (isPoliciesOnlyPull && log.jsonDataIn?.Policies?.length) return log
          // Return log if array of clients has been found
          if (isClientAndPoliciesPull && !_.isEmpty(log.jsonDataIn) && !isPoliciesOnlyPull) return log
          throw new Error('pending');
        }),
        retryWhen((errors) =>
          errors.pipe(
            concatMap((e, i) => {
              // Call every 5 seconds with a timeout of 5 minutes
              return iif(() => i > 60 || i > retries, throwError(e), of(e).pipe(delay(5000)))
            })
          )
        ),
        takeUntil(this.destroyed$),
      )
  }

  pollJobStatusById(id: any, type?: IDataPullType) {
    let pollCount = type?.loadSeconds ?? 6;
    if(type?.loadSeconds) { pollCount = type.loadSeconds }
    return this.http
      .get(`${this.apiUrl}/poll/job-status-by-job-id/${id}`, this.options)
      .pipe(
        map((job: { status: boolean; mappings: any[]; organization: string; log?: { result: 'Success' | 'Failed' } }) => {
          if (job.status && job.mappings) return { mappings: job.mappings, organization: job.organization };
          this.i += 1;
          throw job.status;
        }),
        retryWhen((errors) =>
          errors.pipe(
            concatMap((e, i) =>
              iif(() => i >= pollCount, throwError(e), of(e).pipe(delay(1000)))
            )
          )
        )
      );
  }

  startNewForm(oldFormId: string, newFormId: string, clientId: number) {
    const body = {
      oldFormId,
      newFormId,
      clientId,
    };
    return this.http.post(`${this.apiUrl}/map-to-xilo-form`, body).pipe(
      map((res) => {
        return res['obj'];
      })
    );
  }

  getExistingQuote(orgId:string, submissionId:number | string, clientId:number):Observable<any> {
    return this.http.get<Observable<any>>(`${this.apiUrl}/org/${orgId}/client/${clientId}/submission/${submissionId}`)
  }

  createRateRequest(body:RateRequest):Observable<RateRequest> {
    console.log('WE IN')
    return this.http.post<RateRequest>(`${this.workflowApiUrl}/org/${body.orgId}/rate-requests`, body)
  }

  getRateRequestJobs(companyId: number, requestId: number):Observable<any> {
    return this.http.get<Observable<any>>(`${this.workflowApiUrl}/org/${companyId}/rate-requests/${requestId}?with-jobs=true`)
  }

  getClientJobs(companyId: number, clientId: number):Observable<any> {
    return this.http.get<Observable<any>>(`${this.apiUrl}/rating/${companyId}/client/${clientId}/jobs`)
  }

  getIntegrationMappingById(id: number):Observable<any> {
    return this.http.get<Observable<any>>(`${this.apiUrl}/get-by-id/${id}`)
  }

  pollRateRequestJobs(companyId: number, requestId: number) {
    return this.getRateRequestJobs(companyId, requestId).pipe(
      map((res:RateRequest) => {
        console.log('Polling Rater: %o', res);
        if (res.status === 'Failed' || res.status === 'Done' || res.status === 'Cancelled') return res
        throw new Error('pending');
      }),
      retryWhen((errors) =>
        errors.pipe(
          concatMap((e, i) => {
            // Call every 5 seconds with a timeout of 5 minutes
            return iif(() => i > 60 || e?.message?.includes('failed'), throwError(e), of(e).pipe(delay(5000)))
          })
        )
      ),
      takeUntil(this.destroyed$),
    )
  }

  constructRaterErrors(jobs: RateJob[]) {
    let message = 'Rater Failed';
    for (const job of jobs) {
      if (job.error) message += ` ${job.error.toString()}`
    }
    return message;
  }

  public getFormIntegrationsMapping(formId: string): Observable<IntegrationMapping[]> {
    return getCachedRequest(
      this.http.get(
        this.apiUrl + '/list-by-form?formId=' + formId).pipe(
        map((response: { integrationMappings: IntegrationMapping[] }) => response?.integrationMappings ?? [] as IntegrationMapping[])),
      formId,
      this.cachedGetFormIntegrationsMapping,
      this.writeRequestEvent$,
    );
  }

  public createIntegrationMapping(formId: string, vendorId: number, vendor?: Vendor, mapping?: IVendorMapping[]): Observable<IntegrationMapping> {
    return this.http.post(this.apiUrl, { jsonMapping: mapping ?? [], formId: formId, vendorId },).pipe(
      exhaustMap((response: IntegrationMappingResponse) => {
        if (response.obj !== null && response.integrationMapping !== null) {
          const integrationMapping = { ...response.integrationMapping };
          if (!integrationMapping.vendor && vendor && vendor.id === vendorId) {
            integrationMapping.vendor = vendor;
          }
          return of(integrationMapping);
        }
        return throwError(() => new Error('Unable to create mapping: ' + (response.message ?? '')));
      })
    );
  }

  public getVendorsList(): Observable<Vendor[]> {
    return getCachedRequest(
      this.http.get(this.vendorSchemaUrl + '/list-all-active').pipe(
        map((response: { vendors: Vendor[] }) => response.vendors)
      ),
      '/list-all-active',
      this.cachedGetVendorsList,
      this.writeRequestEvent$,
    );
  }

  public updateIntegrationMapping(integrationMapping: IntegrationMapping) {
    return this.http.put(
      `${this.apiUrl}/${integrationMapping.id}?formId=${integrationMapping.formId}`,
      {
        jsonMapping: integrationMapping.jsonMapping,
        isActive: integrationMapping.isActive,
        order: integrationMapping.order,
      }
    );
  }

  public removeIntegrationMapping(integrationMapping: IntegrationMapping) {
    return this.http.delete(`${this.apiUrl}/${integrationMapping.id}`);
  }
}
